/**
 * EventsServicesDisplay - Main page for Events & Functions category
 * Features:
 * - Service-first hierarchical categories
 * - Event type filters (Weddings, Birthday, Kids, Corporate, Religious)
 * - Intent-based actions (Book Venue, Hire DJ, Get Catering Quote)
 * - Provider cards by service type
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { 
  Search, Filter, MapPin, Star, Clock, ChevronRight, ChevronDown, ChevronUp,
  Building, Utensils, Camera, Music, Palette, Package, ClipboardList,
  Heart, Cake, Baby, Briefcase, Sparkles, Calendar,
  Users, Loader2, Check, X, PartyPopper, Eye
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category Hierarchy
const CATEGORY_HIERARCHY = {
  venues: {
    name: 'Venues',
    icon: Building,
    color: 'purple',
    subcategories: [
      { slug: 'event-venues', name: 'Event Venues', icon: Building }
    ]
  },
  food_hospitality: {
    name: 'Food & Hospitality',
    icon: Utensils,
    color: 'orange',
    subcategories: [
      { slug: 'catering-services', name: 'Catering Services', icon: Utensils }
    ]
  },
  media_entertainment: {
    name: 'Media & Entertainment',
    icon: Camera,
    color: 'pink',
    subcategories: [
      { slug: 'photography-videography', name: 'Photography & Videography', icon: Camera },
      { slug: 'djs-bands', name: 'DJs & Bands', icon: Music }
    ]
  },
  decor_setup: {
    name: 'Decor & Setup',
    icon: Palette,
    color: 'teal',
    subcategories: [
      { slug: 'event-decoration', name: 'Event Decoration', icon: Palette },
      { slug: 'party-equipment-hire', name: 'Party Equipment Hire', icon: Package }
    ]
  },
  planning_coordination: {
    name: 'Planning & Coordination',
    icon: ClipboardList,
    color: 'blue',
    subcategories: [
      { slug: 'event-planning', name: 'Event Planning', icon: ClipboardList }
    ]
  }
};

// Event Type Filters
const EVENT_TYPE_FILTERS = [
  { id: 'weddings', name: 'Weddings', icon: Heart, color: 'pink' },
  { id: 'birthday', name: 'Birthday', icon: Cake, color: 'orange' },
  { id: 'kids', name: 'Kids Parties', icon: Baby, color: 'purple' },
  { id: 'corporate', name: 'Corporate', icon: Briefcase, color: 'blue' },
  { id: 'religious', name: 'Religious', icon: Sparkles, color: 'amber' }
];

// Collapsible Category Group
function CategoryGroup({ group, groupKey, counts, selectedCategory, onCategorySelect, expanded, onToggle }) {
  const Icon = group.icon;
  const colorClasses = {
    purple: 'text-purple-600 bg-purple-100',
    orange: 'text-orange-600 bg-orange-100',
    pink: 'text-pink-600 bg-pink-100',
    teal: 'text-teal-600 bg-teal-100',
    blue: 'text-blue-600 bg-blue-100',
  };
  
  const totalCount = group.subcategories.reduce((sum, sub) => sum + (counts[sub.slug] || 0), 0);
  
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded ${colorClasses[group.color]}`}>
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-medium text-gray-800">{group.name}</span>
          <Badge variant="secondary" className="text-xs">{totalCount}</Badge>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      
      {expanded && (
        <div className="pb-2 pl-4">
          {group.subcategories.map(sub => {
            const SubIcon = sub.icon;
            const count = counts[sub.slug] || 0;
            const isSelected = selectedCategory === sub.slug;
            
            return (
              <button
                key={sub.slug}
                onClick={() => onCategorySelect(sub.slug)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                  isSelected 
                    ? 'bg-purple-50 text-purple-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <SubIcon className="w-4 h-4" />
                  <span>{sub.name}</span>
                </div>
                <span className={isSelected ? 'text-purple-600' : 'text-gray-400'}>{count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Event Type Filter Pills
function EventTypeFilters({ selectedTypes, onToggle }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {EVENT_TYPE_FILTERS.map(filter => {
        const Icon = filter.icon;
        const isSelected = selectedTypes.includes(filter.id);
        const colorClasses = {
          pink: isSelected ? 'bg-pink-500 text-white' : 'bg-pink-50 text-pink-700 hover:bg-pink-100',
          orange: isSelected ? 'bg-orange-500 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100',
          purple: isSelected ? 'bg-purple-500 text-white' : 'bg-purple-50 text-purple-700 hover:bg-purple-100',
          blue: isSelected ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100',
          amber: isSelected ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100',
        };
        
        return (
          <button
            key={filter.id}
            onClick={() => onToggle(filter.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${colorClasses[filter.color]}`}
            data-testid={`event-filter-${filter.id}`}
          >
            <Icon className="w-4 h-4" />
            {filter.name}
          </button>
        );
      })}
    </div>
  );
}

// Event Advertisement Card Component
function EventAdCard({ ad, currencySymbol, onClick }) {
  const navigate = useNavigate();
  
  const categoryColors = {
    'event-venues': 'from-purple-500 to-indigo-600',
    'catering-services': 'from-orange-500 to-red-500',
    'djs-bands': 'from-cyan-500 to-blue-600',
    'photography-videography': 'from-pink-500 to-rose-600',
    'event-decoration': 'from-teal-500 to-emerald-600',
    'party-equipment-hire': 'from-amber-500 to-orange-600',
    'event-planning': 'from-blue-500 to-indigo-600',
  };
  
  const categoryLabels = {
    'event-venues': 'Venue',
    'catering-services': 'Catering',
    'djs-bands': 'DJ / Band',
    'photography-videography': 'Photo / Video',
    'event-decoration': 'Decoration',
    'party-equipment-hire': 'Equipment',
    'event-planning': 'Planning',
  };
  
  const gradientClass = categoryColors[ad.category] || 'from-gray-500 to-gray-600';
  const categoryLabel = categoryLabels[ad.category] || ad.category?.replace(/-/g, ' ');
  
  const handleClick = () => {
    navigate(`/listing/${ad.id}`);
  };
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all overflow-hidden cursor-pointer border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white" 
      onClick={handleClick}
      data-testid={`ad-${ad.id}`}
    >
      <div className="relative h-36 overflow-hidden">
        {ad.images?.[0] ? (
          <img 
            src={ad.images[0]} 
            alt={ad.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-r ${gradientClass}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Ad Badge */}
        <Badge className="absolute top-2 left-2 bg-amber-400 text-amber-900 font-semibold">
          <Sparkles className="w-3 h-3 mr-1" />
          Ad
        </Badge>
        
        {/* Category Badge */}
        <Badge className={`absolute top-2 right-2 bg-gradient-to-r ${gradientClass} text-white border-0`}>
          {categoryLabel}
        </Badge>
        
        {ad.featured && (
          <Badge className="absolute bottom-2 right-2 bg-purple-500 text-white">
            Featured
          </Badge>
        )}
      </div>
      
      <CardContent className="pt-3 pb-4">
        <h3 className="font-bold text-base line-clamp-1">{ad.title}</h3>
        
        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
          {ad.description}
        </p>
        
        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {ad.location || ad.city}
          </span>
          {ad.views > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              {ad.views}
            </span>
          )}
        </div>
        
        {/* Features */}
        {ad.features?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {ad.features.slice(0, 2).map((feature, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-gray-50">
                {feature}
              </Badge>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <div>
            {ad.price_note && <span className="text-gray-500 text-xs">{ad.price_note}</span>}
            <p className="font-bold text-lg text-amber-600">
              {currencySymbol}{ad.price?.toLocaleString()}
            </p>
          </div>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Provider Card Component
function EventProviderCard({ provider, currencySymbol, onBook, onQuote }) {
  const navigate = useNavigate();
  
  const providerTypeColors = {
    VENUE_OWNER: 'from-purple-500 to-indigo-600',
    CATERER: 'from-orange-500 to-red-500',
    PHOTOGRAPHER: 'from-pink-500 to-rose-600',
    DJ: 'from-cyan-500 to-blue-600',
    BAND: 'from-violet-500 to-purple-600',
    DECORATOR: 'from-teal-500 to-emerald-600',
    RENTAL_SHOP: 'from-amber-500 to-orange-600',
    PLANNER: 'from-blue-500 to-indigo-600',
  };
  
  const gradientClass = providerTypeColors[provider.provider_type] || 'from-gray-500 to-gray-600';
  const isQuoteBased = ['CATERER', 'DECORATOR'].includes(provider.provider_type);
  
  return (
    <Card className="group hover:shadow-lg transition-all overflow-hidden" data-testid={`provider-${provider.id}`}>
      <div className={`h-20 bg-gradient-to-r ${gradientClass} relative`}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
            backgroundSize: '15px 15px'
          }} />
        </div>
        {provider.verification_level >= 2 && (
          <Badge className="absolute top-2 right-2 bg-white/20 text-white border-white/30">
            <Check className="w-3 h-3 mr-1" /> Verified
          </Badge>
        )}
        {provider.is_sponsored && (
          <Badge className="absolute top-2 left-2 bg-amber-400 text-amber-900">
            Featured
          </Badge>
        )}
      </div>
      
      <CardContent className="pt-4">
        <h3 className="font-bold text-lg">{provider.provider_name}</h3>
        <p className="text-sm text-gray-500 capitalize">
          {provider.provider_type?.replace(/_/g, ' ')}
        </p>
        
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
          {provider.description}
        </p>
        
        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            {provider.rating?.toFixed(1) || 'New'}
            {provider.review_count > 0 && (
              <span className="text-gray-400">({provider.review_count})</span>
            )}
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {provider.city}
          </span>
        </div>
        
        {/* Event Types */}
        <div className="flex flex-wrap gap-1 mt-3">
          {provider.event_types?.slice(0, 3).map(type => (
            <Badge key={type} variant="outline" className="text-xs capitalize">
              {type}
            </Badge>
          ))}
        </div>
        
        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div>
            <span className="text-gray-500 text-sm">From</span>
            <p className="font-bold text-lg text-purple-600">
              {currencySymbol}{provider.starting_price?.toLocaleString()}
            </p>
          </div>
          {isQuoteBased ? (
            <Button 
              className="bg-gradient-to-r from-orange-500 to-red-500"
              onClick={() => onQuote(provider)}
            >
              Get Quote
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              className="bg-gradient-to-r from-purple-500 to-indigo-600"
              onClick={() => onBook(provider)}
            >
              Book Now
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function EventsServicesDisplay() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();
  
  // State
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [ads, setAds] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [city, setCity] = useState('');
  const [currencySymbol, setCurrencySymbol] = useState(country === 'UK' ? '£' : 'Rs.');
  
  const [expandedGroups, setExpandedGroups] = useState({
    venues: true,
    food_hospitality: true,
    media_entertainment: true,
    decor_setup: false,
    planning_coordination: false,
  });

  // Toggle category group
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Fetch categories and counts
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/events/categories?country=${country}`);
        if (response.ok) {
          const data = await response.json();
          setCategoryCounts(data.counts || {});
          setCurrencySymbol(data.currency_symbol || (country === 'UK' ? '£' : 'Rs.'));
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [country]);

  // Fetch providers and ads
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Build params for providers
        const providerParams = new URLSearchParams({ country, limit: '30' });
        if (selectedCategory) providerParams.append('service_type', selectedCategory);
        if (selectedEventTypes.length === 1) providerParams.append('event_type', selectedEventTypes[0]);
        if (city && city !== 'all') providerParams.append('city', city);
        
        // Build params for ads - fetch from event subcategories
        const eventCategories = ['event-venues', 'catering-services', 'djs-bands', 'photography-videography', 
                                  'event-decoration', 'party-equipment-hire', 'event-planning'];
        const adsCategory = selectedCategory || eventCategories.join(',');
        const adsParams = new URLSearchParams({ 
          country, 
          page_size: '20',
          status: 'active'
        });
        if (selectedCategory) {
          adsParams.append('category', selectedCategory);
        }
        if (city && city !== 'all') adsParams.append('city', city);
        
        // Fetch both in parallel
        const [providersRes, adsRes] = await Promise.all([
          fetch(`${API_URL}/api/events/providers?${providerParams}`),
          fetch(`${API_URL}/api/listings/?${adsParams}`)
        ]);
        
        if (providersRes.ok) {
          const data = await providersRes.json();
          setProviders(data.providers || []);
        }
        
        if (adsRes.ok) {
          const adsData = await adsRes.json();
          // Filter ads to only include event-related categories
          // Note: API returns 'listings' not 'items'
          const allAds = adsData.listings || adsData.items || [];
          const eventAds = allAds.filter(ad => 
            eventCategories.includes(ad.category)
          );
          setAds(eventAds);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [country, selectedCategory, selectedEventTypes, city]);

  // Handle category selection
  const handleCategorySelect = (slug) => {
    if (selectedCategory === slug) {
      setSelectedCategory(null);
      setSearchParams({});
    } else {
      setSelectedCategory(slug);
      setSearchParams({ category: slug });
    }
  };

  // Toggle event type filter
  const toggleEventType = (typeId) => {
    setSelectedEventTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  // Handle booking
  const handleBook = (provider) => {
    navigate(`/events/book/${provider.id}?type=${provider.service_type}`);
  };

  // Handle quote request
  const handleQuote = (provider) => {
    navigate(`/events/quote/${provider.id}?type=${provider.service_type}`);
  };

  // Filter providers
  const filteredProviders = useMemo(() => {
    let filtered = providers;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.provider_name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }
    
    if (selectedEventTypes.length > 0) {
      filtered = filtered.filter(p => 
        p.event_types?.some(t => selectedEventTypes.includes(t))
      );
    }
    
    return filtered;
  }, [providers, searchQuery, selectedEventTypes]);
  
  // Filter ads based on selected category and search
  const filteredAds = useMemo(() => {
    let filtered = ads;
    
    // Map selectedCategory to ad category
    const categoryMap = {
      'VENUE_OWNER': 'event-venues',
      'CATERER': 'catering-services',
      'DJ': 'djs-bands',
      'BAND': 'djs-bands',
      'PHOTOGRAPHER': 'photography-videography',
      'VIDEOGRAPHER': 'photography-videography',
      'DECORATOR': 'event-decoration',
      'EQUIPMENT_RENTAL': 'party-equipment-hire',
      'EVENT_PLANNER': 'event-planning'
    };
    
    if (selectedCategory) {
      const adCategory = categoryMap[selectedCategory] || selectedCategory;
      filtered = filtered.filter(ad => ad.category === adCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ad => 
        ad.title?.toLowerCase().includes(query) ||
        ad.description?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [ads, selectedCategory, searchQuery]);

  const totalProviders = Object.values(categoryCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-orange-500 text-white py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <PartyPopper className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Events & Functions</h1>
              <p className="text-purple-200">
                Venues, catering, entertainment, and everything for your perfect event
              </p>
            </div>
          </div>
          
          {/* Search Bar */}
          <div className="flex gap-3 mt-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input 
                placeholder="Search venues, caterers, DJs..."
                className="pl-10 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/60"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button 
              variant="outline" 
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              onClick={() => navigate('/events/packages')}
            >
              <Package className="w-4 h-4 mr-2" />
              Packages
            </Button>
            <Button 
              variant="outline" 
              className="bg-amber-500/20 border-amber-400/30 text-amber-100 hover:bg-amber-500/30"
              onClick={() => navigate('/events/loyalty')}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Rewards
            </Button>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger className="w-40 h-12 bg-white/10 border-white/20 text-white">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {country === 'UK' ? (
                  <>
                    <SelectItem value="London">London</SelectItem>
                    <SelectItem value="Manchester">Manchester</SelectItem>
                    <SelectItem value="Birmingham">Birmingham</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="Colombo">Colombo</SelectItem>
                    <SelectItem value="Kandy">Kandy</SelectItem>
                    <SelectItem value="Galle">Galle</SelectItem>
                    <SelectItem value="Negombo">Negombo</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Event Type Filters */}
        <Card className="mb-6 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Filter by Event Type</h3>
            {selectedEventTypes.length > 0 && (
              <button 
                onClick={() => setSelectedEventTypes([])}
                className="text-sm text-purple-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
          <EventTypeFilters selectedTypes={selectedEventTypes} onToggle={toggleEventType} />
        </Card>
        
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0 hidden lg:block">
            <Card className="sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {/* All Categories */}
                <button
                  onClick={() => handleCategorySelect(null)}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b transition-colors ${
                    !selectedCategory ? 'bg-purple-50 text-purple-700 font-medium' : 'hover:bg-gray-50'
                  }`}
                >
                  <span>All Services</span>
                  <Badge variant="secondary">{totalProviders}</Badge>
                </button>
                
                {/* Category Groups */}
                {Object.entries(CATEGORY_HIERARCHY).map(([key, group]) => (
                  <CategoryGroup
                    key={key}
                    group={group}
                    groupKey={key}
                    counts={categoryCounts}
                    selectedCategory={selectedCategory}
                    onCategorySelect={handleCategorySelect}
                    expanded={expandedGroups[key]}
                    onToggle={() => toggleGroup(key)}
                  />
                ))}
              </CardContent>
            </Card>
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            {/* Active Filters */}
            {(selectedCategory || selectedEventTypes.length > 0) && (
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="text-sm text-gray-500">Active filters:</span>
                {selectedCategory && (
                  <Badge 
                    variant="secondary" 
                    className="cursor-pointer"
                    onClick={() => handleCategorySelect(null)}
                  >
                    {selectedCategory.replace(/-/g, ' ')}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                )}
                {selectedEventTypes.map(type => (
                  <Badge 
                    key={type}
                    className="bg-purple-100 text-purple-700 cursor-pointer capitalize"
                    onClick={() => toggleEventType(type)}
                  >
                    {type}
                    <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Results Count */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-gray-600">
                {filteredProviders.length} service providers{filteredAds.length > 0 ? ` + ${filteredAds.length} ads` : ''} found
              </p>
              <Select defaultValue="rating">
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="price">Price: Low to High</SelectItem>
                  <SelectItem value="recent">Most Recent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : (filteredProviders.length > 0 || filteredAds.length > 0) ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Interleave ads with providers - show 2 ads first, then providers */}
                {filteredAds.slice(0, 2).map(ad => (
                  <EventAdCard
                    key={`ad-${ad.id}`}
                    ad={ad}
                    currencySymbol={currencySymbol}
                  />
                ))}
                {filteredProviders.slice(0, 6).map(provider => (
                  <EventProviderCard
                    key={provider.id}
                    provider={provider}
                    currencySymbol={currencySymbol}
                    onBook={handleBook}
                    onQuote={handleQuote}
                  />
                ))}
                {/* More ads after first batch of providers */}
                {filteredAds.slice(2, 4).map(ad => (
                  <EventAdCard
                    key={`ad-${ad.id}`}
                    ad={ad}
                    currencySymbol={currencySymbol}
                  />
                ))}
                {/* Remaining providers */}
                {filteredProviders.slice(6).map(provider => (
                  <EventProviderCard
                    key={provider.id}
                    provider={provider}
                    currencySymbol={currencySymbol}
                    onBook={handleBook}
                    onQuote={handleQuote}
                  />
                ))}
                {/* Remaining ads at the end */}
                {filteredAds.slice(4).map(ad => (
                  <EventAdCard
                    key={`ad-${ad.id}`}
                    ad={ad}
                    currencySymbol={currencySymbol}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <PartyPopper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No providers found</h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your filters or search query
                </p>
                <Button onClick={() => {
                  setSelectedCategory(null);
                  setSelectedEventTypes([]);
                  setSearchQuery('');
                }}>
                  Clear All Filters
                </Button>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
